<template>
  <v-card>
    <v-toolbar>
      <v-toolbar-title>
        {{ pageTitle }}
        <v-chip v-if="organization" class="ma-2" close @click:close="clearOrganization()">
          {{
            organization.legacyOrganizationKey
              ? organization.legacyOrganizationKey + "&nbsp;-&nbsp;"
              : organization.organizationKey
              ? organization.organizationKey + "&nbsp;-&nbsp;"
              : ""
          }}
          {{ organization.name }}
        </v-chip>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-form @submit.stop.prevent="onSubmit">
        <v-text-field
          dense
          class="mr-2"
          v-model="search"
          :label="$i18n.translate('Filter') + ' ' + $i18n.translate('Participants')"
          flat
          solo-inverted
          hide-details
          clearable
          clear-icon="mdi-close-circle-outline"
          @click:clear="onClear"
        ></v-text-field>
      </v-form>
      <v-menu offset-y :close-on-content-click="false">
        <template v-slot:activator="{ on: onMenu }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on: onTooltip }">
              <v-btn class="mr-2" small fab v-on="{ ...onMenu, ...onTooltip }">
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </template>
            <span>{{ $i18n.translate("Advanced Search") }}</span>
          </v-tooltip>
        </template>
        <v-list>
          <v-list-item-group color="primary">
            <v-subheader>{{ $i18n.translate("Advanced Search") }}</v-subheader>
            <v-list-item selectable>
              <v-list-item-content>
                <v-select
                  dense
                  :items="effectiveItems"
                  v-model="filterByEffective"
                  :label="$i18n.translate('Active') + ' / ' + $i18n.translate('Inactive')"
                  clearable
                  @input="onSubmit"
                ></v-select>
              </v-list-item-content>
            </v-list-item>
            <v-list-item selectable>
              <v-list-item-content>
                <v-select
                  dense
                  :items="profileItems"
                  v-model="filterByProfileComplete"
                  :label="$i18n.translate('Profile Complete') + ' / ' + $i18n.translate('Profile Incomplete')"
                  clearable
                  @input="onSubmit"
                ></v-select>
              </v-list-item-content>
            </v-list-item>
            <v-list-item selectable v-if="!this.$kawasaki.isRacerAdmin()">
              <v-list-item-content>
                <ParticipantTypesField
                  dense
                  v-model="filterByParticipantTypes"
                  :label="$i18n.translate('Participant Types')"
                  @input="onSubmit"
                ></ParticipantTypesField>
              </v-list-item-content>
            </v-list-item>
            <v-list-item selectable>
              <v-list-item-content>
                <CustomFilter
                  :label="$i18n.translate('Participant') + ' Status'"
                  v-model="filterByParticipantStatus"
                  :options="participantStatusOption"
                  @input="onSubmit"
                />
              </v-list-item-content>
            </v-list-item>
            <v-list-item selectable>
              <v-list-item-content>
                <DateTimePickerField
                  v-model="filterByLastLoginFrom"
                  :label="$i18n.translate('Last Login From')"
                  :hint="$i18n.translate('Filter by last login start date')"
                  @input="onSubmit"
                  clearable
                />
              </v-list-item-content>
            </v-list-item>

            <v-list-item selectable>
              <v-list-item-content>
                <DateTimePickerField
                  v-model="filterByLastLoginTo"
                  :label="$i18n.translate('Last Login To')"
                  :hint="$i18n.translate('Filter by last login end date')"
                  @input="onSubmit"
                  clearable
                />
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>
      <v-tooltip bottom v-if="hasFilters">
        <template v-slot:activator="{ on: onTooltip }">
          <v-btn class="mr-2" small fab v-on="{ ...onTooltip }" @click="onClearSearch">
            <v-icon>mdi-magnify-close</v-icon>
          </v-btn>
        </template>
        <span>{{ $i18n.translate("Clear Search") }}</span>
      </v-tooltip>
      <v-select
        class="show-columns"
        label="Show Columns"
        dense
        :items="showHeaders"
        v-model="selectedHeaders"
        clearable
        multiple
        hide-details
        flat
        solo-inverted
        return-object
        @change="initHeaders"
      >
        <template v-slot:selection="data">{{ showSelected(data) }}</template>
      </v-select>
    </v-toolbar>
    <v-card-text>
      <v-card v-if="organization" dark class="mb-4">
        <v-row>
          <v-col lg="6" md="6" sm="12" xs="12">
            <v-card-title>
              <span>
                {{
                  organization.legacyOrganizationKey
                    ? organization.legacyOrganizationKey + "&nbsp;-&nbsp;"
                    : organization.organizationKey
                    ? organization.organizationKey + "&nbsp;-&nbsp;"
                    : ""
                }}
              </span>
              <span>{{ organization.name }}</span>
            </v-card-title>
            <v-card-text>
              <span v-if="organizationType">
                {{ organizationType.name }}
                {{
                  organization.legacyOrganizationKey
                    ? organization.legacyOrganizationKey
                    : organization.organizationKey
                    ? organization.organizationKey
                    : ""
                }}
                <span v-if="parentOrganizations">
                  <span v-for="parentOrganization in parentOrganizations" :key="parentOrganization.id">
                    <v-icon small>mdi-arrow-right</v-icon> {{ parentOrganization.name }}
                  </span>
                </span>
              </span>
              <div v-if="organization.address">{{ organization.address.fullAddress }}</div>
              <div v-if="organization.phoneNumber1">{{ organization.phoneNumber1 | toPhoneNumber }}</div>
              <div v-if="total != null">{{ total }} {{ total == 1 ? "person" : "people" }}</div>
            </v-card-text>
          </v-col>
          <v-col lg="6" md="6" sm="12" xs="12">
            <div class="mt-4">
              <CustomFieldValues
                v-for="item in nonEmptyCustomFields.slice(0, 3)"
                :key="`field-${item.index}`"
                :value="item.value"
                :customField="item.field"
              />
              <v-expand-transition>
                <div v-if="showMore">
                  <CustomFieldValues
                    v-for="item in nonEmptyCustomFields.slice(3)"
                    :key="`hidden-${item.index}`"
                    :value="item.value"
                    :customField="item.field"
                  />
                </div>
              </v-expand-transition>
              <div v-if="nonEmptyCustomFields.slice(3).length > 0" class="ps-4 pt-2">
                <v-btn variant="text" size="small" @click="toggleShowMore" class="text-primary">
                  <template v-if="showMore">
                    Show Less
                    <v-icon size="18" class="ms-1">mdi-chevron-up</v-icon>
                  </template>
                  <template v-else>
                    +{{ nonEmptyCustomFields.slice(3).length }} more
                    <v-icon size="18" class="ms-1">mdi-chevron-down</v-icon>
                  </template>
                </v-btn>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>
      <AlertDisplay ref="alertRef" />
      <v-data-table
        :items="items"
        :headers="headers"
        :server-items-length="total"
        :options.sync="options"
        :loading="loading"
        mobile-breakpoint="800"
        :items-per-page="options.itemsPerPage"
        :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
      >
        <template v-slot:item.user.firstName="{ item }"> {{ item.user.fullName }} </template>
        <template v-slot:item.organization="{ item }">
          <span v-if="item.organization">
            {{
              item.organization
                ? item.organization.legacyOrganizationKey
                  ? item.organization.legacyOrganizationKey + " - "
                  : item.organization.organizationKey + " - "
                : ""
            }}
            {{ item.organization.name }}
            <div v-if="!loading && getRelatedOrganizations(item.id) != null">
              <span v-for="relatedOrganization in getRelatedOrganizations(item.id)" :key="relatedOrganization.id">
                {{
                  relatedOrganization.relatedOrganization.legacyOrganizationKey
                    ? relatedOrganization.relatedOrganization.legacyOrganizationKey + " - "
                    : relatedOrganization.relatedOrganization.organizationKey + " - "
                }}
                {{ relatedOrganization.relatedOrganization.name }} <br />
              </span>
            </div>
          </span>
        </template>
        <template v-slot:item.user.username="{ item }">
          <div>
            <div>{{ item.user.username }}</div>
            <div v-if="item.user.legacyUsername" class="legacy-username">
              {{ item.user.legacyUsername }}
            </div>
          </div>
        </template>
        <template v-slot:item.user.lastSuccessfulLoginDate="{ item }">
          {{ item.user.lastSuccessfulLoginDate | formatDateFromNow }}
        </template>
        <template v-slot:item.requiredFieldsMissing="{ item }">
          <v-icon v-if="item.requiredFieldsMissing.length === 0" color="green">mdi-check</v-icon>
          <v-tooltip v-else bottom>
            <template #activator="{ on, attrs }">
              <v-icon color="red" v-bind="attrs" v-on="on">mdi-close</v-icon>
            </template>
            <span> Missing: {{ item.requiredFieldsMissing.join(", ") }} </span>
          </v-tooltip>
        </template>
        <template v-slot:item.canImpersonate="{ item }">
          <v-icon v-if="hasImpersonatePrivilege(item.user)" color="green">mdi-check</v-icon>
          <v-icon v-else color="red">mdi-close</v-icon>
        </template>
        <template
          v-for="index in selectedProgram.participantCustomFields.length"
          v-slot:[getCustomFieldSlotName(index)]="{ item }"
        >
          <span :key="index">
            {{
              $customField.renderValue(
                selectedProgram.participantCustomFields[index - 1],
                item["customFieldValue" + index]
              )
            }}
          </span>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-container class="pa-0" fluid>
            <v-row class="flex-nowrap text-no-wrap" no-gutters align="center">
              <v-col cols="auto" class="mr-2">
                <v-tooltip bottom v-if="cantImpersonateReason(item)">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn class="mr-2" icon small color="warning" v-bind="attrs" v-on="on">
                      <v-icon>mdi-account-switch-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ cantImpersonateReason(item) }}</span>
                </v-tooltip>
                <v-tooltip bottom v-else>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      class="mr-2"
                      icon
                      small
                      @click="onImpersonate(item)"
                      v-bind="attrs"
                      v-on="on"
                      :loading="globalLoading"
                    >
                      <v-icon>mdi-account-switch-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Impersonate</span>
                </v-tooltip></v-col
              >
              <v-col cols="auto">
                <v-menu v-if="item.status.name === 'ENR'">
                  <template #activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" aria-label="Actions">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="onResetPassword(item)">
                      <v-list-item-icon><v-icon>mdi-key-variant</v-icon></v-list-item-icon>
                      <v-list-item-title>{{ $i18n.translate("Reset Password") }} </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="onCancelReps(item)">
                      <v-list-item-icon><v-icon>mdi-cancel</v-icon></v-list-item-icon>
                      <v-list-item-title>{{ $i18n.translate("Cancel Registration") }} </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="openRevisionDialog(item)">
                      <v-list-item-icon><v-icon>mdi-history</v-icon></v-list-item-icon>
                      <v-list-item-title>{{ $i18n.translate("Revision History") }} </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="openEditableDialog(item)">
                      <v-list-item-icon><v-icon>mdi-pencil</v-icon></v-list-item-icon>
                      <v-list-item-title>{{ $i18n.translate("Edit Custom Fields") }} </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-col>
            </v-row>
          </v-container>
        </template>
      </v-data-table>
      <v-dialog v-model="showRevisionDialog" max-width="800px" scrollable>
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span class="text-h6">Revisions</span>
            <v-btn icon @click="closeRevisionDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-tabs v-model="tab">
              <v-tab> {{ $i18n.translate("Participant Revisions") }}</v-tab>
              <v-tab>{{ $i18n.translate("User Revisions") }}</v-tab>
            </v-tabs>
            <v-tabs-items v-model="tab">
              <v-tab-item>
                <RevisionTimeline type="participant" :id="itemSelected.id" />
              </v-tab-item>
              <v-tab-item>
                <RevisionTimeline type="user" :id="itemSelected.user.id" />
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog v-if="dialog" v-model="dialog" max-width="600px" persistent>
        <v-card>
          <v-card-title class="headline">{{ selectedActionHeader }}</v-card-title>
          <v-card-text>
            <p v-html="selectedActionDescription"></p>
            <v-row v-if="selectedActionType === actionTypes.CANCELLING">
              <v-col cols="12">
                <RejectionReasonField
                  :selectedRejectReasonValue="selectedRejectParticipantReason"
                  :rejectReason="rejectParticipantReason"
                  :allowOther="true"
                  label="Select Reason for Cancellation"
                  otherReasonLabel="Other Reason for Cancellation"
                  @update:selectedRejectReason="updateRejectionReason"
                  rejectionType="PARTICIPANT"
              /></v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="onCancel()">{{ $i18n.translate("Cancel") }}</v-btn>
            <v-btn
              :disabled="selectedActionType === actionTypes.CANCELLING && validRejectParticipantReason"
              color="primary"
              @click="onSubmit()"
              :loading="loading"
              >Agree</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-if="showCustomFieldDialog" v-model="showCustomFieldDialog" max-width="800px" scrollable>
        <v-card>
          <v-card-title class="headline">Edit Field(s) Value</v-card-title>
          <v-card-text class="mt-4">
            <v-form v-model="valid" ref="customfieldForm">
              <template v-if="loading">
                <div class="d-flex justify-center m-6">
                  <v-progress-circular :size="30" indeterminate color="primary"></v-progress-circular>
                </div>
              </template>
              <template v-else>
                <template v-for="customField in participantCustomFields">
                  <CustomField
                    :key="customField.id"
                    v-model="itemSelected[`customFieldValue${customField.programIndex + 1}`]"
                    :customField="customField"
                  />
                </template>
              </template>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="onCancel()">{{ $i18n.translate("Cancel") }}</v-btn>
            <v-btn
              color="primary"
              @click="onUpdateEditableCustomField()"
              :disabled="!valid || loading"
              :loading="loading"
              >Update</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-text>
  </v-card>
</template>

<style>
.show-columns {
  max-width: 20%;
}
</style>

<script>
import { mapGetters } from "vuex";
import ParticipantTypesField from "../../gapp-components/components/fields/ParticipantTypesField.vue";
import CustomFilter from "@/gapp-components/components/fields/CustomFilter.vue";
import DateTimePickerField from "@/gapp-components/components/fields/DateTimePickerField.vue";
import AlertDisplay from "@/gapp-components/components/display/AlertDisplay.vue";
import RejectionReasonField from "@/gapp-components/components/fields/RejectionReasonField.vue";
import CustomFieldValues from "@/gapp-components/components/fields/CustomFieldValues.vue";
import RevisionTimeline from "@/gapp-components/components/tables/RevisionTimeline.vue";
import CustomField from "@/gapp-components/components/fields/CustomField";
import CustomFieldService from "@/gapp-components/services/customField.service.js";
import ParticipantService from "@/gapp-components/services/participant.service.js";
import moment from "moment-timezone";
import UtilService from "@/gapp-components/services/util.service.js";

export default {
  components: {
    ParticipantTypesField,
    CustomFilter,
    DateTimePickerField,
    AlertDisplay,
    RejectionReasonField,
    CustomFieldValues,
    RevisionTimeline,
    CustomField
  },
  name: "ParticipantManagement",
  metaInfo: {
    title: "Salesperson Management"
  },
  data() {
    return {
      search: "",
      filterByParticipantTypes: [],
      filterByParticipantStatus: { value: "ENR" },
      itemSelected: { user: {} },
      originalItemSelected: { user: {} },
      actionTypes: {
        CANCELLING: "cancelling",
        RESET_PASSWORD: "reset_password"
      },
      selectedActionType: null,
      dialog: false,
      rejectReason: undefined,
      rejectReasonValid: false,
      errors: {},
      rules: {
        reason: [v => !!v || "Reason is required"]
      },
      rejectParticipantReason: "",
      selectedRejectParticipantReason: "",

      loading: false,
      items: [],
      nameFilter: "",
      headers: [],
      total: 0,
      options: {
        itemsPerPage: 10,
        page: 1
      },
      organization: null,
      organizationType: null,
      parentOrganizations: null,
      filterByEffective: "Active",
      effectiveItems: [
        { text: "Active", value: "Active" },
        { text: "Inactive", value: "Inactive" },
        { text: "All", value: "All" }
      ],
      filterByProfileComplete: undefined,
      filterByLastLoginFrom: null,
      filterByLastLoginTo: null,
      profileItems: [
        { text: "Profile Complete", value: false },
        { text: "Profile Incomplete", value: true }
      ],
      showHeaders: [],
      selectedHeaders: [],
      firstFetch: false,
      relatedOrganizations: null,
      showMore: false,
      showRevisionDialog: false,
      tab: 0,
      showCustomFieldDialog: false,
      participantCustomFields: [],
      valid: false
    };
  },
  watch: {
    options: {
      handler() {
        if (this.firstFetch) {
          this.fetchData();
        }
      }
    }
  },
  methods: {
    onResetPassword(item) {
      this.itemSelected = item;
      this.selectedActionType = this.actionTypes.RESET_PASSWORD;
      this.dialog = true;
    },
    onCancelReps(item) {
      this.itemSelected = item;
      this.selectedActionType = this.actionTypes.CANCELLING;
      this.dialog = true;
    },

    showSelected(v) {
      if (v.index == 0) {
        return this.selectedHeaders.length + " Selected";
      }
      return "";
    },
    openRevisionDialog(item) {
      this.showRevisionDialog = true;
      this.itemSelected = item;
    },
    closeRevisionDialog() {
      this.showRevisionDialog = false;
      this.tab = 0;
    },
    async fetchCustomFieldsParticipantTypes(participantTypeId) {
      this.participantCustomFields = await CustomFieldService.fetchParticipantCustomFields(
        this.$api,
        participantTypeId,
        this.selectedProgram.participantCustomFields
      );
      this.loading = false;
    },
    openEditableDialog(item) {
      this.itemSelected = item;
      this.originalItemSelected = { ...item };
      this.showCustomFieldDialog = true;
      this.loading = true;
      this.fetchCustomFieldsParticipantTypes(item.participantType.id);
    },
    async onUpdateEditableCustomField() {
      const isValid = this.$refs.customfieldForm.validate();
      if (!isValid) return;
      this.loading = true;

      if (this.originalItemSelected.customFieldValue17 !== this.itemSelected.customFieldValue17) {
        this.itemSelected.customFieldValue18 = true;
      }

      const payload = Object.keys(this.itemSelected)
        .filter(key => key.startsWith("customFieldValue"))
        .reduce((obj, key) => {
          let value = this.itemSelected[key];
          if (Array.isArray(value)) {
            value = value.join("|");
          }
          obj[key] = value;
          return obj;
        }, {});

      return this.$api
        .patch("/api/participants/" + this.itemSelected.id, payload)
        .then(() => {
          this.onCancel();
        })
        .catch(error => {
          this.errors = this.$api.getErrorsFromResponse(error);
        })
        .finally(() => {
          this.fetchData();
        });
    },
    initShowHeaders() {
      this.showHeaders = [];

      let size = this.selectedProgram.participantCustomFields.length;
      for (let i = 1; i <= size; i++) {
        let participantCustomField = this.selectedProgram.participantCustomFields[i - 1];
        if (participantCustomField.name) {
          this.showHeaders.push({
            value: "customFieldValue" + i,
            text: this.$i18n.translate(participantCustomField.name),
            sortable: true,
            align: this.$customField.alignValue(participantCustomField)
          });
        }
      }
    },
    initHeaders() {
      this.headers = [];
      this.headers.push({
        value: "participantKey",
        text: this.$i18n.translate("Key"),
        sortable: true
      });
      this.headers.push({
        value: "user.firstName",
        text: this.$i18n.translate("Name"),
        sortable: true
      });
      this.headers.push({
        value: "user.username",
        text: this.$i18n.translate("Username"),
        sortable: false
      });
      this.headers.push({
        value: "organization",
        text: this.$i18n.translate("Organization"),
        sortable: true
      });
      this.headers.push({
        value: "participantType.name",
        text: this.$i18n.translate("Type"),
        align: "end",
        sortable: false
      });
      this.headers.push({
        value: "status.description",
        text: this.$i18n.translate("Status"),
        align: "end",
        sortable: true
      });
      this.headers.push({
        value: "user.lastSuccessfulLoginDate",
        text: this.$i18n.translate("Last Login"),
        align: "end",
        sortable: true
      });
      this.headers.push({
        value: "requiredFieldsMissing",
        text: this.$i18n.translate("Profile Complete?"),
        align: "center",
        sortable: false
      });
      if (this.$privilege.hasPrivilege("PARTICIPANT_IMPERSONATE")) {
        this.headers.push({
          value: "canImpersonate",
          text: this.$i18n.translate("Impersonate Privilege"),
          align: "center",
          sortable: false
        });
      }

      let size = this.selectedHeaders.length;
      for (let i = 0; i < size; i++) {
        this.headers.push(this.selectedHeaders[i]);
      }

      if (this.$privilege.hasPrivilege("PARTICIPANT_IMPERSONATE")) {
        this.headers.push({
          value: "actions",
          text: this.$i18n.translate("Actions"),
          align: "center",
          sortable: false
        });
      }
    },

    getCustomFieldSlotName(index) {
      let fieldValue = "item.customFieldValue" + index;
      return fieldValue;
    },
    onClear() {
      this.search = "";
      this.fetchData();
    },
    onSubmit() {
      this.options.page = 1;
      this.$refs.alertRef?.resetAlert();
      switch (this.selectedActionType) {
        case this.actionTypes.CANCELLING:
          return this.cancelReps();
        case this.actionTypes.RESET_PASSWORD:
          return this.resetPassword();
      }
      this.fetchData();
    },

    async cancelReps() {
      this.loading = true;

      let patchForm = {};
      patchForm.cancelledReason =
        this.selectedRejectParticipantReason == "Other"
          ? this.rejectParticipantReason
          : this.selectedRejectParticipantReason;
      patchForm.status = { name: "CAN" };

      patchForm.expirationDate = moment()
        .tz(this.selectedClient?.timezone)
        .format("YYYY-MM-DDTHH:mm:ss.SSSZ");

      return this.$api
        .patch("/api/participants/" + this.itemSelected.id, patchForm)
        .then(async () => {
          await this.logCancellation(patchForm.cancelledReason);
          await this.onCancel();
        })
        .catch(error => {
          this.errors = this.$api.getErrorsFromResponse(error);
        })
        .finally(() => {
          this.fetchData();
        });
    },
    async logCancellation(reason) {
      const cancelledReason = reason;
      const message = `<strong>Action:</strong> Participant Cancelled <br/><strong>Reason:</strong> ${cancelledReason}`;
      await ParticipantService.logParticipant(this.itemSelected, message, this.selectedParticipant.user);
    },

    resetPassword() {
      this.loading = true;

      let postForm = {};
      postForm.username = this.itemSelected.user.username;

      return this.$api
        .post("/api/users/resetPassword", postForm)
        .then(() => {
          this.onCancel();
        })
        .catch(error => {
          this.errors = this.$api.getErrorsFromResponse(error);
        })
        .finally(() => {
          this.loading = false;
          this.$nextTick(() => {
            this.$refs.alertRef.success("Reset Password Successfully");
          });
        });
    },
    updateRejectionReason({ selectedRejectReasonValue, rejectReason }) {
      this.selectedRejectParticipantReason = selectedRejectReasonValue;
      this.rejectParticipantReason = rejectReason;
    },
    onCancel() {
      this.dialog = false;
      this.rejectReasonValid = false;
      this.selectedRejectParticipantReason = "";
      this.rejectParticipantReason = "";
      this.search = "";
      this.errors = {};
      this.showCustomFieldDialog = false;
      this.participantCustomFields = [];
    },

    onClearSearch() {
      this.search = "";
      this.filterByEffective = undefined;
      this.filterByProfileComplete = undefined;
      this.filterByParticipantTypes = [];
      this.filterByLastLoginFrom = null;
      this.filterByLastLoginTo = null;
      this.onSubmit();
    },
    clearOrganization() {
      this.$router.push({ name: "companyManagement" });
    },
    fetchData() {
      this.loading = true;
      if (this.$route.query.organizationId) {
        if (this.organization) {
          return Promise.all([
            this.fetchOrganizationType(),
            this.fetchParticipants(this.organization).then(() => {
              return this.fetchRelatedOrganizations();
            }),
            this.fetchParentOrganizations()
          ]).finally(() => {
            this.loading = false;
            this.firstFetch = true;
          });
        } else {
          return this.$api.get("/api/organizations/" + this.$route.query.organizationId).then(({ data }) => {
            this.organization = data;
            return Promise.all([
              this.fetchOrganizationType(),
              this.fetchParticipants(this.organization).then(() => {
                return this.fetchRelatedOrganizations();
              }),
              this.fetchParentOrganizations()
            ]).finally(() => {
              this.loading = false;
              this.firstFetch = true;
            });
          });
        }
      } else {
        return this.fetchParticipants()
          .then(() => {
            return this.fetchRelatedOrganizations();
          })
          .finally(() => {
            this.loading = false;
            this.firstFetch = true;
          });
      }
    },

    fetchOrganizationType() {
      return this.$api
        .get("/api/organizations/" + this.organization.id + "/organizationType")
        .then(({ data }) => {
          this.organizationType = data;
        })
        .catch(() => {
          console.log("No organization type found");
        });
    },

    fetchParentOrganization(organization) {
      return this.$api
        .get("/api/organizations/" + organization.id + "/parentOrganization")
        .then(({ data }) => data)
        .catch(() => {
          console.log("No parent organization found");
        });
    },

    async fetchParentOrganizations() {
      let parents = [];
      if (this.organization && this.organization.id) {
        let parentOrganization1 = await this.fetchParentOrganization(this.organization);
        if (parentOrganization1) {
          parents.push(parentOrganization1);
          let parentOrganization2 = await this.fetchParentOrganization(parentOrganization1);
          if (parentOrganization2) {
            parents.push(parentOrganization2);
            let parentOrganization3 = await this.fetchParentOrganization(parentOrganization2);
            if (parentOrganization3) {
              parents.push(parentOrganization3);
              let parentOrganization4 = await this.fetchParentOrganization(parentOrganization3);
              if (parentOrganization4) {
                parents.push(parentOrganization4);
              }
            }
          }
        }
      }

      this.parentOrganizations = parents;
      return Promise.resolve();
    },

    fetchParticipantType(key) {
      return this.$api.post("/api/participantTypes/search?size=1", { participantTypeKey: key }).then(({ data }) => {
        return data && data.content.length == 1 ? data.content[0] : null;
      });
    },

    fetchRelatedOrganizations() {
      this.relatedOrganizations = new Map();
      let promises = [];
      for (let i = 0; i < this.items.length; i++) {
        promises.push(this.fetchRelatedOrganizationsForParticipant(this.items[i]));
      }
      return Promise.all(promises).then(() => {
        this.$forceUpdate();
      });
    },

    fetchRelatedOrganizationsForParticipant(participant) {
      return this.$api
        .post("/api/relatedOrganizations/search?size=25&sort=relatedOrganization.organizationKey", {
          participant: { id: participant.id }
        })
        .then(({ data }) => {
          this.relatedOrganizations.set(participant.id, data.content);
        });
    },

    getRelatedOrganizations(id) {
      return this.relatedOrganizations != null ? this.relatedOrganizations.get(id) : null;
    },

    fetchParticipants(org) {
      let filters = {};
      if (this.search && this.search.length > 0) {
        filters.keyword = this.search;
      }

      filters.isEffective = this.filterByEffective;
      filters.missingRequiredInformation = this.filterByProfileComplete;

      let currentFiscalYear = this.$client.getCurrentFiscalYear(this.selectedClient).fiscalYear;
      let isCurrentFiscalYear = this.selectedFiscalYear?.fiscalYear == currentFiscalYear;

      if (!isCurrentFiscalYear && this.$route.query.participantTypeKey != undefined) {
        filters.enrolledAfter = this.selectedFiscalYear?.fiscalYearStart;
        filters.enrolledBefore = this.selectedFiscalYear?.fiscalYearEnd;
      }

      if (this.filterByParticipantTypes && this.filterByParticipantTypes.length > 0) {
        filters.participantTypes = this.filterByParticipantTypes.map(participantType => {
          return {
            id: participantType.id
          };
        });
      }

      if (this.filterByLastLoginFrom) {
        filters.lastSuccessfulLoginDateAfter = this.filterByLastLoginFrom;
      }

      if (this.filterByLastLoginTo) {
        filters.lastSuccessfulLoginDateBefore = this.filterByLastLoginTo;
      }

      if (this.filterByParticipantStatus) {
        filters.status = { name: this.filterByParticipantStatus.value };
      }

      if (org) {
        filters.organization = { id: org.id };
      }

      if (this.$kawasaki.isRacerAdmin()) {
        filters.participantTypeKey = "400";
      }

      const params = UtilService.buildPaginationParams(this.options);

      return this.$api.post(`/api/participants/search?${params.toString()}`, filters).then(({ data }) => {
        this.loading = false;
        this.items = data.content;
        this.total = data.totalElements;
      });
    },
    hasImpersonatePrivilege(user) {
      return this.$privilege.hasImpersonatePrivilegeForUser(user);
    },
    cantImpersonateReason(participant) {
      if (this.selectedParticipant && this.selectedParticipant.id == participant.id) {
        return "Can't impersonate yourself";
      }
      if (this.impersonationParticipant && this.impersonationParticipant.id == participant.id) {
        return "Already impersonating user";
      }
      if (!this.$privilege.hasImpersonatePrivilege()) {
        return "Don't have impersonating privilege";
      }
      if (this.$privilege.hasImpersonatePrivilegeForUser(participant.user)) {
        return "Can't impersonate one who has impersonate privilege";
      }
      // Allowed to impersonate
      return undefined;
    },
    async onImpersonate(participant) {
      this.$store.dispatch("startGlobalLoading");
      try {
        const { data } = await this.$api.get("/api/participants/byParticipantId/" + participant.id);
        await this.$auth.startImpersonation(data);
        await this.$router.push({ name: "dashboard" });
      } catch (error) {
        console.error("Error during impersonation:", error);
      } finally {
        this.$store.dispatch("stopGlobalLoading");
      }
    },
    toggleShowMore() {
      this.showMore = !this.showMore;
    }
  },
  created() {
    if (this.$route.query.participantTypeKey != undefined) {
      this.fetchParticipantType(this.$route.query.participantTypeKey).then(participantType => {
        if (participantType) {
          this.filterByParticipantTypes.push(participantType);
        }
        this.onSubmit();
      });
    } else {
      this.onSubmit();
    }
  },
  mounted() {
    this.initShowHeaders();
    this.initHeaders();

    if (this.$route.query.active != undefined) {
      this.filterByEffective = parseInt(this.$route.query.active) == 1;
    }

    this.$store.dispatch("setBreadcrumb", [
      {
        text: this.$i18n.translate("Dashboard"),
        to: { name: "dashboard" },
        exact: true
      },
      {
        text: "Admin"
      },
      { text: "Participant Management" }
    ]);
  },
  computed: {
    ...mapGetters([
      "impersonationParticipant",
      "selectedParticipant",
      "selectedProgram",
      "selectedClient",
      "selectedFiscalYear",
      "globalLoading"
    ]),
    hasFilters() {
      let check =
        (this.search && this.search.length > 0) ||
        this.filterByEffective != undefined ||
        this.filterByProfileComplete !== undefined ||
        (this.filterByParticipantTypes && this.filterByParticipantTypes.length > 0);
      return check;
    },
    participantStatusOption() {
      return [
        {
          value: "PRE",
          text: "Pre-Enrolled"
        },
        {
          value: "ENR",
          text: "Enrolled"
        },
        {
          value: "CAN",
          text: "Cancelled"
        },
        {
          value: "PRE_CAN",
          text: "Pre-Cancelled"
        }
      ];
    },
    selectedActionHeader() {
      let header = "";
      switch (this.selectedActionType) {
        case this.actionTypes.CANCELLING:
          header = "Are you sure you want to cancel this participant?";
          break;
        case this.actionTypes.RESET_PASSWORD:
          header = "Are you sure you want to reset password?";
          break;
      }

      return header;
    },
    validRejectParticipantReason() {
      if (this.selectedRejectParticipantReason != "" && this.selectedRejectParticipantReason.length > 0) {
        if (this.selectedRejectParticipantReason == "Other" && this.rejectParticipantReason != "") {
          return false;
        } else {
          if (this.selectedRejectParticipantReason != "Other") {
            return false;
          }
        }
      }
      return true;
    },
    selectedActionDescription() {
      let description = "";
      switch (this.selectedActionType) {
        case this.actionTypes.CANCELLING:
          description =
            "An email notification will be sent to the participant email address notifying them of the cancellation.";
          break;
        case this.actionTypes.RESET_PASSWORD:
          description = `${this.itemSelected.user.username} will be sent an email containing a temporary password, along with instructions to create a new one`;
          break;
      }

      return description;
    },
    nonEmptyCustomFields() {
      return this.selectedProgram.organizationCustomFields
        .map((field, index) => {
          const value = this.organization[`customFieldValue${index + 1}`];
          if (value !== null && value !== "") {
            return {
              index: index + 1,
              field,
              value
            };
          }
          return null;
        })
        .filter(Boolean);
    },
    pageTitle() {
      const entity = this.$kawasaki.isRacerAdmin() ? "Racer" : "Participant";

      return this.$i18n.translate(entity) + " " + this.$i18n.translate("Management");
    }
  }
};
</script>

<style>
.legacy-username {
  font-size: 1em;
  color: gray;
}
</style>
